import React, { useRef } from 'react';

/**
 * N single-digit boxes that behave like one field: paste spreads, backspace walks back,
 * arrows move, Enter submits. Controlled via `value` (string of digits) / `onChange`.
 */
const OtpBoxes = ({ length = 6, value = '', onChange, onSubmit, disabled = false, error = false, autoFocus = false, ariaLabel = 'One-time code' }) => {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const focus = (i) => {
    const el = refs.current[Math.min(Math.max(i, 0), length - 1)];
    if (el) {
      el.focus();
      el.select?.();
    }
  };
  const emit = (arr) => onChange?.(arr.join('').slice(0, length));

  const handleChange = (i, raw) => {
    const clean = raw.replace(/\D/g, '');
    if (!clean) {
      const next = [...digits];
      next[i] = '';
      emit(next);
      return;
    }
    const next = [...digits];
    clean.split('').forEach((d, k) => {
      if (i + k < length) next[i + k] = d;
    });
    emit(next);
    focus(i + clean.length);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      const next = [...digits];
      next[i - 1] = '';
      emit(next);
      focus(i - 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focus(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focus(i + 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.();
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    e.preventDefault();
    emit(text.split(''));
    focus(text.length);
  };

  return (
    <div className="flex items-center gap-2" role="group" aria-label={ariaLabel}>
      {digits.map((d, i) => (
        <React.Fragment key={i}>
          {i === Math.floor(length / 2) && <span className="h-px w-3 bg-border" aria-hidden="true" />}
          <input
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            disabled={disabled}
            autoFocus={autoFocus && i === 0}
            aria-label={`Digit ${i + 1}`}
            className={`h-12 w-10 rounded-md border bg-white text-center font-mono text-lg font-semibold text-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 sm:h-14 sm:w-12 ${
              error ? 'border-destructive' : d ? 'border-primary/50' : 'border-border'
            }`}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

export default OtpBoxes;
